import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { disableSendDefaultMessage, setEditableMessage } from '../../actions/chat/actions';
import { setHintQuestionsHidden } from '../../actions/chatHintQuestions';
import { sendFirstMessage } from '../../actions/firstMessage';
import { sendMessage } from '../../actions/sendMessage';
import { Captcha } from '../../components/Captcha';
import { MessageInputField } from '../../components/MessageInputField';
import {
  chatListSelector,
  chatWindowSelector,
  currentOpenChatSelector,
  hostTypeSelector,
  isShowSubAgentChatListSelector,
  offerSelector,
} from '../../selectors';
import { agentIdSelector } from '../../selectors/agentSelector';
import { saveChatMessageHint } from '../../services/saveChatMessageHint';
import { EAvailableActions } from '../../types/availableActions';
import { EAnalyticsKey } from '../../types/chatHint';
import { EFirstMessageStatus } from '../../types/firstMessageStatus';
import { EHostType } from '../../types/hostType';
import { ERoute } from '../../types/route';
import { useApplicationContext } from '../../utils/applicationContext';
import { isFileUploadButtonEnabled } from '../../utils/isFileUploadButtonEnabled';
import { isSendUserModifiedSuggestionsEnabled } from '../../utils/isSendUserModifiedSuggestionsEnabled';
import { AttachFileButtonContainer } from '../AttachFileButtonContainer';
import { HintQuestionsContainer } from '../HintQuestionsContainer';

interface IMessageInputFieldContainerProps {
  onHeightChange(): void;
}

export const MessageInputFieldContainer: React.VFC<IMessageInputFieldContainerProps> = ({
  onHeightChange,
}: IMessageInputFieldContainerProps) => {
  const dispatch = useDispatch();

  const offer = useSelector(offerSelector);
  const agentId = useSelector(agentIdSelector);
  const currentChat = useSelector(currentOpenChatSelector);
  const hostType = useSelector(hostTypeSelector);
  const { isBubbleInputFocused } = useSelector(chatWindowSelector);
  const isShowSubAgentChatList = useSelector(isShowSubAgentChatListSelector);
  const chatList = useSelector(chatListSelector);

  const { config } = useApplicationContext();

  const [captchaToken, setCaptchaToken] = React.useState<string | undefined>(undefined);
  const [analyticsKey, setAnalyticsKey] = React.useState<EAnalyticsKey | null>(null);

  const { chatId, availableActions, firstMessageStatus, editableMessage, sendDefaultMessage } = currentChat || {};
  const { id: offerId } = offer || {};

  const sitekey = config.get<string>('mobileSearch.firstMessage.recaptchaSiteKey');
  const isIframe = hostType === EHostType.Frame;

  const isAttachButtonEnabled =
    isFileUploadButtonEnabled(config) && availableActions?.includes(EAvailableActions.SendFile);
  const isSendMessageEnabled = isIframe || availableActions?.includes(EAvailableActions.SendMessage);
  const isSendModifiedSuggestionsEnabled = isSendUserModifiedSuggestionsEnabled(config);
  const isShowCaptcha = firstMessageStatus === EFirstMessageStatus.NeedCaptcha;

  const onSend = React.useCallback(
    ({ text, chatId }: { text: string; chatId: string }) => {
      if (offerId && analyticsKey && text && isSendModifiedSuggestionsEnabled) {
        dispatch(saveChatMessageHint({ offer, analyticsKey, messageText: text }));
      }
      dispatch(setHintQuestionsHidden({ chatId }));

      if (chatId === 'blob' && offerId) {
        dispatch(sendFirstMessage({ text, offerId, captchaToken }));

        return;
      }
      if (chatId === 'blob' && agentId) {
        dispatch(sendFirstMessage({ text, captchaToken, agentId }));

        return;
      }

      dispatch(sendMessage(text, chatId));
    },
    [offerId, analyticsKey, isSendModifiedSuggestionsEnabled, dispatch, agentId, offer, captchaToken],
  );

  const onHintQuestionChange = React.useCallback((analyticsKey: EAnalyticsKey) => {
    setAnalyticsKey(analyticsKey);
  }, []);

  React.useEffect(() => {
    if (editableMessage && sendDefaultMessage && chatId) {
      dispatch(disableSendDefaultMessage({ chatId }));
      if (chatId !== 'blob') {
        dispatch(setEditableMessage({ chatId, text: '' }));
      }
      onSend({ text: editableMessage, chatId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatId, offerId, editableMessage, sendDefaultMessage]);

  if (!chatId || isBubbleInputFocused || chatList.currentRoute === ERoute.Spam) {
    return null;
  }

  return (
    <MessageInputField
      hintQuestions={<HintQuestionsContainer onHintQuestionChange={onHintQuestionChange} />}
      chatId={chatId}
      isLoading={firstMessageStatus === EFirstMessageStatus.Pending}
      isDisabled={!isSendMessageEnabled}
      onSend={onSend}
      attachFileButton={isAttachButtonEnabled ? <AttachFileButtonContainer /> : null}
      captcha={
        isShowCaptcha && (
          <Captcha
            handleCaptchaTokenChange={
              /* istanbul ignore next */ (value: string | null) => setCaptchaToken(value || undefined)
            }
            sitekey={sitekey}
          />
        )
      }
      onHeightChange={onHeightChange}
      placeholder={isShowSubAgentChatList ? 'Написать сообщение от лица агента' : ''}
    />
  );
};
